// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {WHATS_NEW_ENTRIES, type WhatsNewEntry} from '@app/features/app/components/whats_new/WhatsNewEntries';
import styles from '@app/features/app/components/whats_new/WhatsNewModal.module.css';
import {BLUESKY_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {BlueskyIcon} from '@app/features/ui/components/icons/BlueskyIcon';
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import WhatsNew from '@app/features/ui/state/WhatsNew';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {ExternalUrls} from '@app/vendor/constants/ExternalUrls';
import {getFormattedLongDate} from '@app/vendor/date_utils/DateFormatting';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon} from '@phosphor-icons/react';
import {useCallback, useEffect, useRef, useState} from 'react';

const WHATS_NEW_MODAL_KEY = 'whats-new';
export function openWhatsNewModal(): void {
	const latestEntry = WHATS_NEW_ENTRIES[0];
	if (!latestEntry) return;
	ModalCommands.pushWithKey(
		modal(() => <WhatsNewModal entry={latestEntry} />),
		WHATS_NEW_MODAL_KEY,
	);
}
interface WhatsNewModalProps {
	entry: WhatsNewEntry;
}
function formatEntryDate(date: Date): string {
	return getFormattedLongDate(date, getCurrentLocale());
}
export function WhatsNewModal({entry}: WhatsNewModalProps) {
	const {t} = useLingui();
	const initialFocusRef = useRef<HTMLDivElement | null>(null);
	const scrollerRef = useRef<ScrollerHandle | null>(null);
	const [isScrollHintVisible, setIsScrollHintVisible] = useState(false);
	const handleDismiss = useCallback(() => {
		WhatsNew.dismiss(entry.id);
		ModalCommands.pop();
	}, [entry.id]);
	const updateScrollHintVisibility = useCallback(() => {
		const scrollerNode = scrollerRef.current?.getScrollerNode();
		if (!scrollerNode) {
			setIsScrollHintVisible(false);
			return;
		}
		const maxScroll = Math.max(0, scrollerNode.scrollHeight - scrollerNode.clientHeight);
		const distanceFromBottom = maxScroll - scrollerNode.scrollTop;
		const shouldShowScrollHint = maxScroll > 16 && scrollerNode.scrollTop < 72 && distanceFromBottom > 24;
		setIsScrollHintVisible(shouldShowScrollHint);
	}, []);
	const handleContentScroll = useCallback(() => {
		updateScrollHintVisibility();
	}, [updateScrollHintVisibility]);
	const handleContentResize = useCallback(() => {
		updateScrollHintVisibility();
	}, [updateScrollHintVisibility]);
	const handleScrollToBottom = useCallback(() => {
		scrollerRef.current?.scrollToBottom({animate: true});
	}, []);
	useEffect(() => {
		updateScrollHintVisibility();
	}, [entry.id, updateScrollHintVisibility]);
	return (
		<Modal.Root
			size="medium"
			className={styles.whatsNewRoot}
			centered
			onClose={handleDismiss}
			initialFocusRef={initialFocusRef}
		>
			<Modal.ScreenReaderLabel text={t`What's New`} />
			<div className={styles.contentShell}>
				<Modal.Content
					padding="none"
					ref={scrollerRef}
					fade={false}
					onScroll={handleContentScroll}
					onResize={handleContentResize}
				>
					<div className={styles.contentBody} ref={initialFocusRef} tabIndex={-1}>
						<img src={entry.coverImage} alt="" width={1920} height={1080} className={styles.coverImage} />
						<div className={styles.dateLine}>{formatEntryDate(entry.date)}</div>
						<div className={`${markupStyles.markup} ${styles.markdownBody}`}>
							<SafeMarkdown content={entry.content} options={{context: MarkdownContext.STANDARD_WITHOUT_JUMBO}} />
						</div>
					</div>
				</Modal.Content>
				<button
					type="button"
					className={isScrollHintVisible ? `${styles.scrollHint} ${styles.scrollHintVisible}` : styles.scrollHint}
					aria-hidden={!isScrollHintVisible}
					aria-label={t`Scroll to bottom`}
					tabIndex={isScrollHintVisible ? 0 : -1}
					onClick={handleScrollToBottom}
				>
					<div className={styles.scrollHintCircle}>
						<CaretDownIcon size={16} weight="bold" />
					</div>
				</button>
			</div>
			<Modal.Footer className={styles.footer}>
				<a href={ExternalUrls.BLUESKY} target="_blank" rel="noopener noreferrer" className={styles.blueskyLink}>
					<BlueskyIcon size={16} className={styles.blueskyIcon} />
					{t`Follow us on ${BLUESKY_PROVIDER_NAME}`}
				</a>
				<Button variant="primary" fitContent className={styles.dismissButton} onClick={handleDismiss}>
					{t`Got it!`}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
}
